import { useMemo } from 'react'

import { ModalContext } from '@domain-group/suit-theme'

import { useFilters, useModals, useSetState } from '@domain-group/react-hooks'

import PageContext from '@contexts/pageContext'
import type { PageContextPayload } from '@contexts/pageContext'

import CreateOrEditImageLibraryModal from '@modals/CreateOrEditImageLibraryModal'
import CreateOrEditImageModal from '@modals/CreateOrEditImageModal'

import handleCallbackAction from '@functions/handleCallbackAction'
import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

import type { ImageLibraryModel, ImageModel } from '@models/types'

import PageContent from './components/PageContent'

const defaultState = {
  showCreateOrEditImageLibraryModal: false,
  showCreateOrEditImageModal: false,
}

type SetState = (state: Partial<typeof defaultState>) => void

const callbacks = (component: string, setState: SetState) => {
  const componentCallbacks = {
    CreateOrEditImageModal: {
      closeModal: () => setState({ showCreateOrEditImageModal: false }),
      deleteImage: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      setCoverImage: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
    CreateOrEditImageLibraryModal: {
      closeModal: () => setState({ showCreateOrEditImageLibraryModal: false }),
      createImageLibrary: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      deleteImageLibrary: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      updateImageLibrary: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
  }

  // @ts-expect-error TS7053
  return componentCallbacks[component] || {}
}

const ImageLibraryList = () => {
  const [state, setState] = useSetState(defaultState)
  const { showCreateOrEditImageLibraryModal, showCreateOrEditImageModal } = state

  const {
    callbacks: filterCallbacks,
    defaultFilters,
    pageFilters,
  } = useFilters()

  const modalContext = useModals({ setState })
  const {
    callbacks: { setModalData },
  } = modalContext

  const pageContext: PageContextPayload = useMemo(() => ({
    callbacks: {
      ...filterCallbacks,
      showCreateOrEditImageModal: (payload: { image: ImageModel }) => {
        setModalData('CreateOrEditImageModal', payload)
        setState({ showCreateOrEditImageModal: true })
      },
      showCreateOrEditImageLibraryModal: (payload: { imageLibrary: Partial<ImageLibraryModel>}) => {
        setModalData('CreateOrEditImageLibraryModal', payload)
        setState({ showCreateOrEditImageLibraryModal: true })
      },
    },
    defaultFilters,
  }), [])

  return (
    <PageContext.Provider value={pageContext}>
      <ModalContext.Provider value={modalContext}>
        <PageContent pageFilters={pageFilters} />

        <CreateOrEditImageModal
          callbacks={callbacks('CreateOrEditImageModal', setState)}
          showModal={showCreateOrEditImageModal}
        />

        <CreateOrEditImageLibraryModal
          callbacks={callbacks('CreateOrEditImageLibraryModal', setState)}
          showModal={showCreateOrEditImageLibraryModal}
        />
      </ModalContext.Provider>
    </PageContext.Provider>
  )
}

export default ImageLibraryList
